import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import mesIcon from "../../../assets/img/icons/mesIcon.svg";
import otherIcon from "../../../assets/img/icons/otherIcon.svg";
import profIcon from "../../../assets/img/icons/profIcon.svg";
import protocolsIcon from "../../../assets/img/icons/protocolsIcon.svg";
import socIcon from "../../../assets/img/icons/socIcon.svg";
import striIcon from "../../../assets/img/icons/striIcon.svg";
import { useSiteData } from "../../../SiteDataContext";

const Hero: React.FC = () => {
  const { t } = useTranslation();
  const { siteData, setSiteData } = useSiteData();
  const setSiteDataRef = useRef(setSiteData);
  const handlerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    setSiteDataRef.current = setSiteData;
  }, [setSiteData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const iconElement = document.getElementById("iIcon");
      const prevHandler = handlerRef.current;
      const handleIconClick = () => {
        setSiteDataRef.current((prevData) => ({
          ...prevData,
          isOpenPolicyModal: true,
        }));
      };
      handlerRef.current = handleIconClick;
      if (prevHandler && iconElement) {
        iconElement.removeEventListener("click", prevHandler);
      }
      iconElement?.addEventListener("click", handleIconClick);
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [siteData]);

  return (
    <main className="w-full" data-testid="hero-main">
      <section className="w-full flex justify-center">
        <div className="container pl-1-25 md:plr-3">
          <h2 className=" lt-0-01 md:lt-0-04 fw-900 md:text-5xl font-bold mt-6 md:mt-10-7 mb-12 md:mb-12">
            {siteData?.main?.title}
          </h2>
          <article className="md:w-42">
            <div
              className="ck-content"
              dangerouslySetInnerHTML={{
                __html: siteData?.main?.content || "",
              }}
            />
          </article>

          <div className="flex justify-center md:justify-start mb-7-5 md:mb-17-5">
            <Link
              to="/choose"
              className="br-0-5 main-button f-size-1-125 md:f-size-1-5 md:lt-0-02"
            >
              {t("hero.report_an_outage")}
            </Link>
          </div>
        </div>
      </section>

      <section className="w-full flex justify-center">
        <div className="container pl-1-25 bg-cream-light br-t-1-25 md:br-1-25 w-full p-2-875 pt-2-5 md:pt-3-3 pb-3 md:plr-8">
          <h3 className="text-3xl font-bold mb-1 text-center md:lt-0-08">
            {siteData?.whatWeCollect?.title}
          </h3>

          <div
            className="ck-content mb-4 m-auto"
            style={{ maxWidth: "800px" }}
            dangerouslySetInnerHTML={{
              __html: siteData?.whatWeCollect?.content || "",
            }}
          />

          <h4 className="color-green text-2xl leading-none font-bold mb-1-5 md:mb-2-1 text-center md:lt-0-08">
            {siteData?.CardsHeader?.title}
          </h4>

          <div className="grid grid-cols-2 cg-2 rg-3 md:rg-4-4 md:cg-9 md:grid-cols-3">
            <article>
              <img src={protocolsIcon} className="mb-0-75" alt="" />
              <h4 className="f-size-1 md:f-size-1-5 font-bold mb-0-75">
                {t("hero.vpn.title")}
              </h4>
              <p className="f-size-0-875 md:f-size-1-125 lh-1 md:lh-1-375 md:lh-1-4">
                {t("hero.vpn.description")}
              </p>
            </article>

            <article>
              <img src={mesIcon} className="mb-0-75" alt="" />
              <h4 className="f-size-1 md:f-size-1-5 font-bold mb-0-75">
                {t("hero.messengers.title")}
              </h4>
              <p className="f-size-0-875 md:f-size-1-125 lh-1 md:lh-1-375 md:lh-1-4">
                {t("hero.messengers.description")}
              </p>
            </article>
            <article>
              <img src={socIcon} className="mb-0-75" alt="" />
              <h4 className="f-size-1 md:f-size-1-5 font-bold mb-0-75">
                {t("hero.social_networks.title")}
              </h4>
              <p className="f-size-0-875 md:f-size-1-125 lh-1 md:lh-1-375 md:lh-1-4">
                {t("hero.social_networks.description")}
              </p>
            </article>
            <article>
              <img src={striIcon} className="mb-0-75" alt="" />
              <h4 className="f-size-1 md:f-size-1-5 font-bold mb-0-75">
                {t("hero.streaming_services.title")}
              </h4>
              <p className="f-size-0-875 md:f-size-1-125 lh-1 md:lh-1-375 md:lh-1-4">
                {t("hero.streaming_services.description")}
              </p>
            </article>
            <article>
              <img src={profIcon} className="mb-0-75" alt="" />
              <h4 className="f-size-1 md:f-size-1-5 font-bold mb-0-75">
                {t("hero.professional_tools.title")}
              </h4>
              <p className="f-size-0-875 md:f-size-1-125 lh-1 md:lh-1-375 md:lh-1-4">
                {t("hero.professional_tools.description")}
              </p>
            </article>
            <article>
              <img src={otherIcon} className="mb-0-75" alt="" />
              <h4 className="f-size-1 md:f-size-1-5 font-bold mb-0-75">
                {t("hero.other_services.title")}
              </h4>
              <p className="f-size-0-875 md:f-size-1-125 lh-1 md:lh-1-375 md:lh-1-4">
                {t("hero.other_services.description")}
              </p>
            </article>
          </div>
          <div className="flex justify-center md:justify-start mt-16">
            <Link
              to="/choose"
              className="br-0-5 main-button mx-auto f-size-1-125 md:f-size-1-5 md:lt-0-02"
            >
              {t("hero.report_an_outage")}
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Hero;
